import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../utils/cn';

export interface CarouselSlide {
  id: string | number;
  /**
   * Rendered slide body. Used for fully custom slides (Kristy Mart's
   * `bannerSlide()` factory still goes through here so existing banner
   * data renders unchanged).
   */
  content?: ReactNode;
  /**
   * Optional VIDEO URL — when set, the carousel renders a `<video>`
   * element instead of `content` (or alongside it as the background),
   * autoplays it muted, and ADVANCES TO THE NEXT SLIDE when the video
   * ends. While the video is playing, the carousel's normal time-based
   * interval is suspended so the playback isn't cut short.
   *
   * Accepted formats: anything `<video>` plays natively — MP4 (H.264),
   * WebM, OGG. Detect from extension: `.mp4 / .webm / .ogg / .mov`.
   *
   * Mixed playlists are fine — image slides still tick on `interval`,
   * video slides advance on their own `ended` event.
   */
  videoUrl?: string;
  /** Optional poster image for the video (shown until play starts). */
  poster?: string;
  /**
   * Optional overlay rendered on top of the video — same chrome the
   * image slides use (gradient + title + CTA). Used when both
   * `videoUrl` and overlay text are present.
   */
  overlay?: ReactNode;
}

/**
 * Auto-detect `videoUrl` from a `content`-style slide if the slide
 * was built via `bannerSlide({ imageUrl: '...mp4' })`. Falls back to
 * the explicit `videoUrl` field when set.
 */
function isVideoUrl(u: string | undefined): boolean {
  if (!u) return false;
  return /\.(mp4|webm|ogg|ogv|mov|m4v)(\?|#|$)/i.test(u);
}

export interface CarouselProps {
  slides: CarouselSlide[];
  autoPlay?: boolean;
  interval?: number;
  showArrows?: boolean;
  showDots?: boolean;
  /**
   * Fixed pixel height. Use only when you specifically need the
   * banner to be a known height (e.g. for layout above the fold).
   * Prefer `aspectRatio` for responsive banners — it makes the
   * banner scale proportionally with its parent's width.
   */
  height?: number | string;
  /**
   * CSS `aspect-ratio` value (e.g. `'16/5'`, `'21/9'`, `2.5`). When
   * set, `height` is ignored and the banner stretches to fill the
   * parent's width while keeping the ratio. This is the
   * responsive-first option used by the storefront hero banner.
   */
  aspectRatio?: string | number;
  className?: string;
  onSlideChange?: (index: number) => void;
}

export function Carousel({
  slides,
  autoPlay = true,
  interval = 4000,
  showArrows = true,
  showDots = true,
  height = 280,
  aspectRatio,
  className,
  onSlideChange,
}: CarouselProps) {
  const [index, setIndex] = useState(0);
  const hovered = useRef(false);

  const go = useCallback(
    (next: number) => {
      const n = slides.length;
      if (n === 0) return;
      const wrapped = ((next % n) + n) % n;
      setIndex(wrapped);
      onSlideChange?.(wrapped);
    },
    [slides.length, onSlideChange]
  );

  const next = useCallback(() => go(index + 1), [index, go]);
  const prev = useCallback(() => go(index - 1), [index, go]);

  /* Pause the interval-driven autoplay while a video slide is active —
     videos advance themselves via the `<video onEnded>` handler so the
     timer would only cut them short. */
  const activeIsVideo = !!slides[index]?.videoUrl;
  useEffect(() => {
    if (!autoPlay || slides.length < 2 || activeIsVideo) return;
    const id = setInterval(() => {
      if (!hovered.current) setIndex((i) => (i + 1) % slides.length);
    }, interval);
    return () => clearInterval(id);
  }, [autoPlay, interval, slides.length, activeIsVideo]);

  // Swipe support
  const startX = useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => (startX.current = e.touches[0].clientX);
  const onTouchEnd = (e: React.TouchEvent) => {
    if (startX.current == null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    if (dx > 40) prev();
    else if (dx < -40) next();
    startX.current = null;
  };

  return (
    <div
      className={cn(
        'relative w-full overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-card)]',
        className,
      )}
      style={aspectRatio ? { aspectRatio: String(aspectRatio) } : { height }}
      onMouseEnter={() => (hovered.current = true)}
      onMouseLeave={() => (hovered.current = false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-roledescription="carousel"
    >
      <div
        className="flex h-full transition-transform duration-500 ease-out"
        // The flex strip is `slides.length * 100%` wide (e.g. 300% for
        // three slides). Each slide takes `100 / slides.length` percent
        // of that strip, which equals 100% of the viewport.
        // CSS `transform: translateX(...%)` resolves the percentage
        // against the ELEMENT's own width — so translating by
        // `index * 100%` shifts the strip by N viewports per click
        // (only the first slide ever appears centred). The correct
        // shift is `index * (100 / slides.length)` percent of the
        // strip, which equals `index * 100%` of the viewport — i.e.
        // exactly one slide-width per index.
        style={{
          transform: `translateX(-${index * (100 / Math.max(slides.length, 1))}%)`,
          width: `${slides.length * 100}%`,
        }}
      >
        {slides.map((s, i) => (
          <div
            key={s.id}
            className="h-full shrink-0"
            style={{ width: `${100 / slides.length}%` }}
          >
            {/* Video slide — autoplay muted, advance on end. The
                `muted` + `playsInline` combo is required for mobile
                autoplay on iOS / Android. `preload="auto"` so the
                next slide doesn't stall on first frame.
                When the user is on a different slide we pause the
                video to save battery on mobile. */}
            {s.videoUrl ? (
              <div className="relative h-full w-full">
                <video
                  src={s.videoUrl}
                  poster={s.poster}
                  autoPlay={i === index}
                  muted
                  playsInline
                  preload="auto"
                  onEnded={() => {
                    if (i === index) go(index + 1);
                  }}
                  className="absolute inset-0 h-full w-full object-cover"
                />
                {s.overlay ? (
                  <div className="absolute inset-0">{s.overlay}</div>
                ) : null}
                {/* `content` may also be set as an overlay for legacy
                    bannerSlide() callers — both can coexist. */}
                {!s.overlay && s.content ? (
                  <div className="absolute inset-0">{s.content}</div>
                ) : null}
              </div>
            ) : (
              s.content
            )}
          </div>
        ))}
      </div>

      {showArrows && slides.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous slide"
            onClick={prev}
            className="absolute left-3 top-1/2 -translate-y-1/2 inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur hover:bg-black/60"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            aria-label="Next slide"
            onClick={next}
            className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur hover:bg-black/60"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}

      {showDots && slides.length > 1 && (
        <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
          {slides.map((s, i) => (
            <button
              key={s.id}
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => go(i)}
              className={cn(
                'h-2 rounded-full transition-all',
                i === index ? 'w-6 bg-white' : 'w-2 bg-white/50 hover:bg-white/70'
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default Carousel;
